/**
 * OIDC Discovery Service
 * 
 * Fetches and caches OpenID Connect discovery configuration
 * Provides dynamic endpoint discovery instead of hardcoded URLs
 */

import { AUTH_URL } from '../config/env';

export interface OidcConfiguration {
  issuer: string;
  authorization_endpoint: string;
  token_endpoint: string;
  userinfo_endpoint: string;
  jwks_uri: string;
  revocation_endpoint: string;
  response_types_supported: string[];
  grant_types_supported: string[];
  code_challenge_methods_supported: string[];
  scopes_supported: string[];
}

let cachedConfig: OidcConfiguration | null = null;
let discoveryPromise: Promise<OidcConfiguration> | null = null;

/**
 * Get OIDC configuration (cached after first fetch)
 */
export async function getOidcConfiguration(): Promise<OidcConfiguration> {
  if (cachedConfig) {
    return cachedConfig;
  }
  
  if (discoveryPromise) {
    return discoveryPromise;
  }
  
  discoveryPromise = fetchOidcConfiguration();
  cachedConfig = await discoveryPromise;
  return cachedConfig;
}

/**
 * Fetch OIDC configuration from well-known endpoint
 */
async function fetchOidcConfiguration(): Promise<OidcConfiguration> {
  const authUrl = AUTH_URL;
  const response = await fetch(`${authUrl}/.well-known/openid-configuration`);
  
  if (!response.ok) {
    throw new Error(`Failed to fetch OIDC configuration: ${response.status}`);
  }
  
  const config = await response.json();
  console.log('[OIDC Discovery] Configuration fetched:', {
    issuer: config.issuer,
    authorization_endpoint: config.authorization_endpoint,
    token_endpoint: config.token_endpoint,
  });
  
  return config;
}

/**
 * Get fallback endpoints if discovery fails
 */
export function getFallbackEndpoints() {
  const authUrl = AUTH_URL;
  return {
    authorization_endpoint: `${authUrl}/oauth/consent`,
    token_endpoint: `${authUrl}/oauth/token`,
    userinfo_endpoint: `${authUrl}/oauth/userinfo`,
    revocation_endpoint: `${authUrl}/oauth/revoke`,
  };
}
